import React, { useState } from 'react';
import { updateScheduleTask } from '../services/scheduleService';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';
import { SCHEDULE_STATUSES, DELAY_CAUSES, formatSlip } from '../constants/schedule';

// One department line's update form - used on Schedule Updates and, for the
// project HOD / PM, inline on the project's schedule (2026-10-26). Status and
// % are kept in step as you edit: 100% = Completed and back, any progress
// moves "Not started" to "In progress" (a line was once saved at 100% while
// still Not started, so it never counted as done). validate() mirrors
// update_task in schedule_routes.py.
// `task` needs: id, status, percent_complete, forecast_end, actual_start,
// actual_end, remark, delay_cause, baseline_end, is_milestone.
const toForm = (t) => ({
  status: t.status,
  percent_complete: Number(t.percent_complete) || 0,
  forecast_end: t.forecast_end || '',
  actual_start: t.actual_start || '',
  actual_end: t.actual_end || '',
  remark: t.remark || '',
  delay_cause: t.delay_cause || '',
});

// Apply one edit plus whatever it implies for the linked field.
const applyField = (f, field, value) => {
  const next = { ...f, [field]: value };
  if (field === 'percent_complete') {
    const pct = Number(value);
    next.percent_complete = pct;
    if (pct === 100) next.status = 'completed';
    else if (f.status === 'completed') next.status = 'in_progress';
    else if (pct > 0 && f.status === 'not_started') next.status = 'in_progress';
  }
  if (field === 'status') {
    if (value === 'completed') next.percent_complete = 100;
    else if (value === 'not_started') { next.percent_complete = 0; next.actual_start = ''; }
    else if (f.percent_complete === 100) next.percent_complete = 95;
  }
  return next;
};

export const validateTaskForm = (f, task, today) => {
  if (f.status === 'not_started' && (f.percent_complete > 0 || f.actual_start)) {
    return 'Work has started - choose In progress (or Completed) instead of Not started';
  }
  if (f.percent_complete === 100 && f.status !== 'completed') return '100% means done - choose Completed and enter the actual finish date';
  if (f.status !== 'not_started' && !f.actual_start) return 'Enter the actual start date';
  if (f.actual_start && f.actual_start > today) return "Actual start can't be in the future";
  if (f.status === 'completed') {
    if (!f.actual_end) return 'Enter the actual finish date';
    if (f.actual_end > today) return "Actual finish can't be in the future";
    if (f.actual_start && f.actual_end < f.actual_start) return 'Actual finish is before actual start';
  }
  if (['delayed', 'on_hold'].includes(f.status) && !f.remark.trim()) return 'Add a remark for a delayed or on-hold task';
  const end = f.status === 'completed' ? f.actual_end : f.forecast_end;
  if (end && task.baseline_end && end > task.baseline_end && (!f.remark.trim() || !f.delay_cause)) {
    return 'Finishing after the baseline date needs a remark and a delay cause';
  }
  return '';
};

const ScheduleTaskForm = ({ task, onSaved, onCancel, successLabel }) => {
  const showToast = useUIStore((s) => s.showToast);
  const [form, setForm] = useState(() => toForm(task));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD in IST

  const onField = (field, value) => { setForm((f) => applyField(f, field, value)); setError(''); };

  const save = async () => {
    const problem = validateTaskForm(form, task, today);
    if (problem) return setError(problem);
    setSaving(true);
    try {
      await updateScheduleTask(task.id, {
        status: form.status,
        percent_complete: form.percent_complete,
        forecast_end: form.status === 'completed' ? null : form.forecast_end || null,
        actual_start: form.actual_start || null,
        actual_end: form.status === 'completed' ? form.actual_end : null,
        remark: form.remark,
        delay_cause: form.delay_cause || null,
      });
      showToast(`${successLabel || 'Task'} updated`, 'success');
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const isCompleted = form.status === 'completed';
  const forecastSlip = !isCompleted && form.forecast_end && task.baseline_end
    ? Math.round((Date.parse(form.forecast_end) - Date.parse(task.baseline_end)) / 86400000) : 0;
  const label = 'block text-xs text-gray-500 mb-1';
  const input = 'w-full border border-gray-300 rounded px-2 py-1.5 text-sm';

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <label>
          <span className={label}>Status</span>
          <select className={input} value={form.status} onChange={(e) => onField('status', e.target.value)}>
            {SCHEDULE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        {!task.is_milestone && (
          <label>
            <span className={label}>% complete: {form.percent_complete}%</span>
            <input type="range" min="0" max="100" step="5" className="w-full mt-2"
              value={form.percent_complete} onChange={(e) => onField('percent_complete', e.target.value)} />
          </label>
        )}
        <label>
          <span className={label}>Actual start</span>
          <input type="date" className={input} value={form.actual_start} onChange={(e) => onField('actual_start', e.target.value)} />
        </label>
        {isCompleted ? (
          <label>
            <span className={label}>Actual finish (required)</span>
            <input type="date" className={input} value={form.actual_end} onChange={(e) => onField('actual_end', e.target.value)} />
          </label>
        ) : (
          <label>
            <span className={label}>Forecast finish</span>
            <input type="date" className={input} value={form.forecast_end} onChange={(e) => onField('forecast_end', e.target.value)} />
            {forecastSlip !== 0 && (
              <span className={`text-xs ${forecastSlip > 0 ? 'text-red-700' : 'text-green-700'}`}>{formatSlip(forecastSlip)} vs baseline</span>
            )}
          </label>
        )}
        <label>
          <span className={label}>Delay cause</span>
          <select className={input} value={form.delay_cause} onChange={(e) => onField('delay_cause', e.target.value)}>
            <option value="">—</option>
            {DELAY_CAUSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>
      </div>
      <div className="flex gap-3 mt-2 items-start flex-wrap md:flex-nowrap">
        <input type="text" className={`${input} flex-1`} placeholder="Remark (required if delayed, on hold or late)"
          value={form.remark} onChange={(e) => onField('remark', e.target.value)} />
        {onCancel && (
          <button onClick={onCancel} className="px-4 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 whitespace-nowrap">Cancel</button>
        )}
        <button onClick={save} disabled={saving}
          className="px-4 py-1.5 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60 whitespace-nowrap">
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
      {task.updated_by_name && (
        <p className="text-xs text-gray-400 mt-1">Last updated by {task.updated_by_name} on {formatDateDDMMYY(task.updated_at?.slice(0, 10))}</p>
      )}
    </div>
  );
};

export default ScheduleTaskForm;
